import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { 
    clientApi, 
    Client, 
    ClientsListParams, 
    CreateClientData, 
    UpdateClientData,
    ClientsListResponse 
} from '../../services/clientApi';

// Async thunks
export const fetchClients = createAsyncThunk(
    'clients/fetchClients',
    async (params: ClientsListParams = {}) => {
        const response = await clientApi.getClients(params);
        return response;
    }
);

export const fetchClient = createAsyncThunk(
    'clients/fetchClient',
    async (id: number) => {
        const response = await clientApi.getClient(id);
        return response;
    }
);

export const createClient = createAsyncThunk(
    'clients/createClient',
    async (clientData: CreateClientData) => {
        const response = await clientApi.createClient(clientData);
        return response;
    }
);

export const updateClient = createAsyncThunk(
    'clients/updateClient',
    async ({ id, clientData }: { id: number; clientData: UpdateClientData }) => {
        const response = await clientApi.updateClient(id, clientData);
        return response;
    }
);

export const deleteClient = createAsyncThunk(
    'clients/deleteClient',
    async (id: number) => {
        await clientApi.deleteClient(id);
        return id;
    }
);

export const toggleClientStatus = createAsyncThunk(
    'clients/toggleClientStatus',
    async (id: number) => {
        const response = await clientApi.toggleClientStatus(id);
        return response;
    }
);

export const approveClient = createAsyncThunk(
    'clients/approveClient',
    async (id: number) => {
        const response = await clientApi.approveClient(id);
        return response;
    }
);

// State interface
interface ClientState {
    clients: Client[];
    selectedClient: Client | null;
    pagination: {
        current_page: number;
        last_page: number;
        per_page: number;
        total: number;
        from: number;
        to: number;
    } | null;
    filters: {
        status: string;
        query: string;
    };
    loading: boolean;
    error: string | null;
}

// Initial state
const initialState: ClientState = {
    clients: [],
    selectedClient: null,
    pagination: null,
    filters: {
        status: '',
        query: '',
    },
    loading: false,
    error: null,
};

// Slice
const clientSlice = createSlice({
    name: 'clients',
    initialState,
    reducers: {
        setFilters: (state, action: PayloadAction<Partial<ClientState['filters']>>) => {
            state.filters = { ...state.filters, ...action.payload };
        },
        clearFilters: (state) => {
            state.filters = {
                status: '',
                query: '',
            };
        },
        clearError: (state) => {
            state.error = null;
        },
        setSelectedClient: (state, action: PayloadAction<Client | null>) => {
            state.selectedClient = action.payload;
        },
    },
    extraReducers: (builder) => {
        // fetchClients
        builder
            .addCase(fetchClients.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchClients.fulfilled, (state, action: PayloadAction<ClientsListResponse>) => {
                state.loading = false;
                state.clients = action.payload.clients;
                state.pagination = action.payload.pagination;
            })
            .addCase(fetchClients.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message || 'Failed to fetch clients';
            });

        // fetchClient
        builder
            .addCase(fetchClient.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchClient.fulfilled, (state, action: PayloadAction<Client>) => {
                state.loading = false;
                state.selectedClient = action.payload;
            })
            .addCase(fetchClient.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message || 'Failed to fetch client';
            });

        // createClient
        builder
            .addCase(createClient.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(createClient.fulfilled, (state, action: PayloadAction<Client>) => {
                state.loading = false;
                state.clients.unshift(action.payload);
                if (state.pagination) {
                    state.pagination.total += 1;
                }
            })
            .addCase(createClient.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message || 'Failed to create client';
            });

        // updateClient
        builder
            .addCase(updateClient.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(updateClient.fulfilled, (state, action: PayloadAction<Client>) => {
                state.loading = false;
                const index = state.clients.findIndex(client => client.id === action.payload.id);
                if (index !== -1) {
                    state.clients[index] = action.payload;
                }
                if (state.selectedClient?.id === action.payload.id) {
                    state.selectedClient = action.payload;
                }
            })
            .addCase(updateClient.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message || 'Failed to update client';
            });

        // deleteClient
        builder
            .addCase(deleteClient.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(deleteClient.fulfilled, (state, action: PayloadAction<number>) => {
                state.loading = false;
                state.clients = state.clients.filter(client => client.id !== action.payload);
                if (state.pagination) {
                    state.pagination.total -= 1;
                }
            })
            .addCase(deleteClient.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message || 'Failed to delete client';
            });

        // toggleClientStatus
        builder
            .addCase(toggleClientStatus.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(toggleClientStatus.fulfilled, (state, action: PayloadAction<{ id: number; name: string; status: number }>) => {
                state.loading = false;
                const index = state.clients.findIndex(client => client.id === action.payload.id);
                if (index !== -1) {
                    state.clients[index].status = action.payload.status;
                }
                if (state.selectedClient?.id === action.payload.id) {
                    state.selectedClient.status = action.payload.status;
                }
            })
            .addCase(toggleClientStatus.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message || 'Failed to toggle client status';
            });

        // approveClient
        builder
            .addCase(approveClient.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(approveClient.fulfilled, (state, action: PayloadAction<Client>) => {
                state.loading = false;
                const index = state.clients.findIndex(client => client.id === action.payload.id);
                if (index !== -1) {
                    state.clients[index] = action.payload;
                }
                if (state.selectedClient?.id === action.payload.id) {
                    state.selectedClient = action.payload;
                }
            })
            .addCase(approveClient.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message || 'Failed to approve client';
            });
    },
});

export const { setFilters, clearFilters, clearError, setSelectedClient } = clientSlice.actions;
export default clientSlice.reducer;
